import i18n from '@/i18n';
import { goodsStudioAPI, taskAPI } from '@/utils/api';
import { GOODS_STUDIO_DEFAULT_PARAMS } from '@/pages/ipe/workbench/constants/aiStudioConfig';
import { resolveGoodsStudioSubmitParams } from '@/pages/ipe/workbench/utils/goodsStudioSubmitUtils';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { invalidateCompositeRowsCache } from '@/pages/ipe/workbench/utils/goodsDetailCompositeMerge';

function stampIndexFromOutput(row) {
  const raw = row?.sourceIndex ?? row?.stampIndex ?? row?.groupIndex;
  const n = Number(raw);
  return Number.isFinite(n) ? n : -1;
}

function filterOutputsForRemake(outputs, stampIndex) {
  return (Array.isArray(outputs) ? outputs : []).filter(
    (row) => stampIndexFromOutput(row) !== stampIndex,
  );
}

function rebuildUrlsFromOutputs(outputs) {
  return (Array.isArray(outputs) ? outputs : [])
    .filter((row) => String(row?.status || '').toLowerCase() === 'success')
    .map((row) => String(row?.url || row?.outputUrl || row?.imageUrl || '').trim())
    .filter(Boolean);
}

function filterCompositeResultsForRemake(forms, stampIndex) {
  return (Array.isArray(forms) ? forms : []).map((form) => ({
    ...form,
    compositeResults: (form.compositeResults || []).filter(
      (row) => Number(row?.groupIndex ?? -1) !== stampIndex,
    ),
  }));
}

function buildCompositeResultsFromOutputs(outputs, groupIndex, baseIndex = 0) {
  return (Array.isArray(outputs) ? outputs : [])
    .map((row, i) => {
      const url = String(row?.url || row?.outputUrl || row?.imageUrl || '').trim();
      if (!url) return null;
      const kind = String(row?.kind || '').trim();
      return {
        compositeUrl: url,
        url,
        groupIndex,
        imageId: String(row?.imageSpaceId || row?.imageId || `ai-out-${baseIndex + i}`),
        status: 'success',
        ...(kind ? { kind } : {}),
        ...(row?.viewUrl ? { viewUrl: row.viewUrl, displayUrl: row.displayUrl || row.viewUrl } : {}),
      };
    })
    .filter(Boolean);
}

function resolveStampUrlForGroup(prevResult, stampIndex, goodsForms) {
  const stampUrls = Array.isArray(prevResult.stampSourceUrls)
    ? prevResult.stampSourceUrls
    : (Array.isArray(prevResult.stampPreviews) ? prevResult.stampPreviews : []);
  const fromList = String(stampUrls[stampIndex] || '').trim();
  if (fromList) return fromList;

  const form = (Array.isArray(goodsForms) ? goodsForms : []).find(
    (f) => String(f?.templateId || '') === 'goods-studio-ai',
  ) || goodsForms?.[0];
  const group = Array.isArray(form?.imageGroups) ? form.imageGroups[stampIndex] : null;
  if (!group || typeof group !== 'object') return '';

  const values = Object.values(group);
  for (let i = 0; i < values.length; i += 1) {
    const ref = values[i];
    const url = String(ref?.url || ref?.previewUrl || ref?.apiUrl || '').trim();
    if (url) return url;
  }
  return '';
}

/**
 * 主图创作详情：在当前任务内对指定印花组重新生成。
 * 走 /goods-studio/submit-and-wait 按 Admin 定价扣 IPE 豆，再写回当前任务（不新建任务、不 force submit）。
 */
export async function remakeGoodsStudioGroupInPlace({
  taskId,
  groupIndex,
  studioParams = {},
}) {
  const id = String(taskId || '').trim();
  const stampIndex = Number(groupIndex);
  if (!id || !Number.isFinite(stampIndex) || stampIndex < 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskOrGroupInvalid'));
  }

  const { task: full } = await taskAPI.getById(id);
  if (!full) throw new Error(i18n.t('workbench:ipeUtilError.taskNotFound'));

  const prevResult = full.result && typeof full.result === 'object' ? full.result : {};
  const resolvedParams = await resolveGoodsStudioSubmitParams({
    ...GOODS_STUDIO_DEFAULT_PARAMS,
    ...(prevResult.studioParams || {}),
    ...studioParams,
  });

  const mainCount = Math.max(1, Number(resolvedParams.mainImageCount) || 1);
  const detailCount = Math.max(0, Number(resolvedParams.detailPageCount) || 0);
  const rawForms = (full.goodsForms?.length ? full.goodsForms : prevResult.goodsForms) || [];
  const stampUrlRaw = resolveStampUrlForGroup(prevResult, stampIndex, rawForms);
  if (!stampUrlRaw) {
    throw new Error(i18n.t('workbench:ipeUtilError.stampGroupNotFound'));
  }

  const stampUrl = await resolveImg2ImgReferenceForApi(stampUrlRaw);
  if (!stampUrl) {
    throw new Error(i18n.t('workbench:ipeUtilError.stampImageUnresolvedRetry'));
  }

  const requestId = `goods-remake-${id}-s${stampIndex}-${Date.now()}`;
  const gen = await goodsStudioAPI.submitAndWait({
    requestId,
    stampImageUrls: [stampUrl],
    params: resolvedParams,
    mainImageCount: mainCount,
    detailPageCount: detailCount,
    modelImageUrl: resolvedParams.modelImageUrl || undefined,
    country: resolvedParams.country,
    countryStyle: resolvedParams.countryStyle,
    language: resolvedParams.language,
    mainImageRatio: resolvedParams.mainImageRatio,
    detailPageRatio: resolvedParams.detailPageRatio,
    productInfo: resolvedParams.productInfo,
    goodsCategory: resolvedParams.goodsCategory,
    modelMode: resolvedParams.modelMode,
    composition: resolvedParams.composition,
    selectedPresetId: resolvedParams.selectedPresetId,
    selectedPresetUrl: resolvedParams.selectedPresetUrl,
    modelType: resolvedParams.modelType,
    modelEthnicity: resolvedParams.modelEthnicity,
  });

  const genOutputs = Array.isArray(gen?.outputs) ? gen.outputs : [];
  const genUrls = Array.isArray(gen?.urls) ? gen.urls : [];
  if (genOutputs.length === 0 && genUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.goodsComposeNoImage'));
  }

  const keptOutputs = filterOutputsForRemake(prevResult.outputs, stampIndex);
  const baseIndex = keptOutputs.length;
  const newOutputs = (genOutputs.length > 0 ? genOutputs : genUrls.map((url, i) => ({
    url,
    kind: i < mainCount ? 'main' : 'detail',
    stampIndex: 0,
    index: i,
  }))).map((row, i) => ({
    ...row,
    sourceIndex: stampIndex,
    stampIndex,
    index: baseIndex + i,
    status: 'success',
  }));

  const mergedOutputs = [...keptOutputs, ...newOutputs];
  const urls = rebuildUrlsFromOutputs(mergedOutputs);
  const newCompositeResults = buildCompositeResultsFromOutputs(newOutputs, stampIndex, baseIndex);
  const goodsForms = filterCompositeResultsForRemake(rawForms, stampIndex).map((form) => ({
    ...form,
    compositeResults: [
      ...(form.compositeResults || []),
      ...newCompositeResults,
    ],
  }));

  const stampUrls = Array.isArray(prevResult.stampSourceUrls)
    ? prevResult.stampSourceUrls
    : (Array.isArray(prevResult.stampPreviews) ? prevResult.stampPreviews : []);
  const perStamp = Math.max(1, mainCount + detailCount);
  const doneStamps = new Set(
    (Array.isArray(prevResult.processedStampIndices) ? prevResult.processedStampIndices : [])
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v >= 0),
  );
  doneStamps.add(stampIndex);

  const successCount = mergedOutputs.filter(
    (o) => String(o?.status || '').toLowerCase() === 'success',
  ).length;
  const failedCount = mergedOutputs.filter(
    (o) => String(o?.status || '').toLowerCase() === 'failed',
  ).length;
  const total = Math.max(
    Number(full.total) || 0,
    stampUrls.length * perStamp,
    successCount + failedCount,
    1,
  );
  const allStampsDone = stampUrls.length === 0 || doneStamps.size >= stampUrls.length;

  const nextResult = {
    ...prevResult,
    studioParams: resolvedParams,
    mainImageCount: mainCount,
    detailPageCount: detailCount,
    processedStampIndices: [...doneStamps].sort((a, b) => a - b),
    outputs: mergedOutputs,
    urls,
    goodsForms,
    batchPhase: allStampsDone ? 'completed' : (prevResult.batchPhase || 'completed'),
    failureHint: '',
    serverWorker: true,
    composeMode: 'ai',
    generationMode: 'goodsStudioAi',
    previewUrl: urls[0] || prevResult.previewUrl || '',
    ...(gen?.engine ? { engine: gen.engine } : {}),
  };

  await taskAPI.update(id, {
    status: failedCount > 0 && successCount === 0 ? 'failed' : 'completed',
    successCount,
    failedCount,
    total,
    result: nextResult,
    goodsForms,
  });

  invalidateCompositeRowsCache(id);
  return { taskId: id, groupIndex: stampIndex, result: nextResult, goodsForms };
}
